/*
 * IMPORTS
 */
import React from "react"; // Npm: react.js library.
import _ from "underscore"; // Npm: Utility module.
import { toast } from "react-hot-toast"; // Npm: React hot toast.
import { useMutation, useQuery } from "@apollo/client"; // Npm: Apollo client.
import { DataGrid } from "@mui/x-data-grid"; // Npm: Material UI Data Grid.
import { HiArrowPath, HiPencil, HiTrash } from "react-icons/hi2"; // Npm: React icons.
import {
  Box,
  Button,
  Checkbox,
  Flex,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Tab,
  TabList,
  Tabs,
  Text,
  Tooltip,
  useBreakpointValue,
  useDisclosure,
} from "@chakra-ui/react"; // Npm: Chakra UI components.

/*
 * PACKAGES
 */
import DeleteCard from "components/DeleteCard";
import Modal from "components/Modal";
import TableSpinner from "components/TableSpinner";

/*
 * GRAPHS
 */
import QueueReadQuery from "./__query__/index.queue.read.query";
import QueueDeleteMutation from "./__mutation__/index.queue.delete.mutation";

/*
 * OBJECTS
 */
const _FormatNumber = (value) => {
  const numericValue = Number(value ?? 0);

  return Number.isFinite(numericValue)
    ? numericValue.toLocaleString("en-IN")
    : "0";
};

const OWNER_TYPES = ["CUSTOMER", "VENDOR"];
const OWNER_TYPE_VALUES = {
  CUSTOMER: "CUSTOMER_ACCOUNT",
  VENDOR: "VENDOR_ACCOUNT",
};
const tabStyles = {
  fontFamily: "DM Sans",
  fontSize: "16px",
  fontStyle: "normal",
  fontWeight: 600,
  letterSpacing: "-0.48px",
  lineHeight: "24px",
  textAlign: "center",
};

const Index = () => {
  // Const assignment.
  const _successFlags = React.useMemo(
    () =>
      Object.React.App.enums.GRAPHQL_SUCCESSFUL_QUERY_FLAGS.enums.map(
        (i) => i.key,
      ),
    [],
  );

  // Hook assignment.
  const [paginationModel, setPaginationModel] = React.useState({
    pageSize: 10,
    page: 0,
  });
  const [ownerTypeIndex, setOwnerTypeIndex] = React.useState(0);
  const [isRefreshing, setIsRefreshing] = React.useState(false);
  const [columnVisibilityModel, setColumnVisibilityModel] = React.useState({
    id: false,
  });
  const [filterModel, setFilterModel] = React.useState({
    items: [],
    logicOperator: "and",
    quickFilterValues: [],
    quickFilterLogicOperator: "and",
  });
  const [selectAll, setSelectAll] = React.useState(false);
  const [selectedIds, setSelectedIds] = React.useState([]);
  const [rowsToDelete, setRowsToDelete] = React.useState([]);
  const [selectedRow, setSelectedRow] = React.useState(null);
  const {
    isOpen: isDeleteModalOpen,
    onOpen: onDeleteModalOpen,
    onClose: onDeleteModalClose,
  } = useDisclosure();
  const _isCurrentViewMobile =
    useBreakpointValue({ base: true, md: false }, { fallback: "md" }) ?? false;
  const _tableHeaderHeightRef = React.useRef(0);
  const _isPreviousPageRef = React.useRef(false);
  const _paginationModelRef = React.useRef(paginationModel);
  const _rowCountRef = React.useRef(0);
  const _setFilterDebounced = React.useMemo(
    () => _.debounce(setFilterModel, 500),
    [],
  );

  React.useEffect(
    () => () => _setFilterDebounced.cancel(),
    [_setFilterDebounced],
  );

  const ownerType = OWNER_TYPE_VALUES[OWNER_TYPES[ownerTypeIndex]];

  const _QueueReadQuery = useQuery(QueueReadQuery, {
    fetchPolicy: _isPreviousPageRef.current
      ? "cache-first"
      : Object.React.App.fetchPolicy,
    variables: {
      ownerType,
      take: paginationModel.pageSize,
      skip: paginationModel.page * paginationModel.pageSize,
    },
  });
  const { refetch } = _QueueReadQuery;
  const [MutationQueueDelete, MutationQueueDeleteResponse] =
    useMutation(QueueDeleteMutation);

  const _queueRead = _QueueReadQuery?.data?.QueueRead;
  const _queueTotalCount = _queueRead?.totalCount;
  const rowCount = React.useMemo(() => {
    if (undefined !== _queueTotalCount && null !== _queueTotalCount)
      _rowCountRef.current = Number(_queueTotalCount);

    return _rowCountRef.current;
  }, [_queueTotalCount]);
  const _hasSuccessfulStatus =
    !_queueRead?.status || _successFlags.includes(_queueRead?.status);

  const _HandleRefresh = React.useCallback(async () => {
    setIsRefreshing(true);

    try {
      await refetch();
    } catch {
      toast.error("Unable to refresh queues. Please try again.");
    } finally {
      setIsRefreshing(false);
    }
  }, [refetch]);

  const _HandleDeleteClose = React.useCallback(() => {
    setSelectedRow(null);
    setRowsToDelete([]);
    onDeleteModalClose();
  }, [onDeleteModalClose]);

  const _HandleDeleteConfirm = React.useCallback(() => {
    const deleteRows = 0 < rowsToDelete.length
      ? rowsToDelete
      : selectedRow ? [selectedRow] : [];

    if (_.isEmpty(deleteRows)) return;

    return MutationQueueDelete({
      variables: {
        input: deleteRows.map((row) => ({
          type: "QUEUE",
          name: row?.name,
          vhost: row?.vhost,
        })),
      },
    })
      .then(async (response) => {
        const failedResponse = response
          ?.data
          ?.QueueDeleteMany
          ?.items
          ?.find((item) => item?.status && !_successFlags.includes(item.status));

        if (failedResponse)
          return toast.error(
            failedResponse?.message ?? "Unable to delete queue.",
          );

        toast.success(
          1 < deleteRows.length ? "Queues deleted." : "Queue deleted.",
        );
        setSelectedIds([]);
        setSelectAll(false);
        await _QueueReadQuery.refetch();
        _HandleDeleteClose();
      })
      .catch(() =>
        toast.error("Something went wrong please try after some time."),
      );
  }, [
    _HandleDeleteClose,
    _QueueReadQuery,
    MutationQueueDelete,
    _successFlags,
    rowsToDelete,
    selectedRow,
  ]);

  const _HandleColumnVisibilityModelChange = React.useCallback(
    (newModel) => setColumnVisibilityModel(newModel),
    [],
  );

  const _HandleOwnerTypeChange = React.useCallback((index) => {
    _isPreviousPageRef.current = false;
    setPaginationModel((current) => {
      const nextPaginationModel = { ...current, page: 0 };

      _paginationModelRef.current = nextPaginationModel;

      return nextPaginationModel;
    });
    setOwnerTypeIndex(index);
  }, []);

  const _HandlePaginationModelChange = React.useCallback(
    (nextPaginationModel) => {
      _isPreviousPageRef.current =
        nextPaginationModel.page < _paginationModelRef.current.page &&
        nextPaginationModel.pageSize === _paginationModelRef.current.pageSize;
      _paginationModelRef.current = nextPaginationModel;
      setPaginationModel(nextPaginationModel);
    },
    [],
  );

  const _skip = paginationModel.page * paginationModel.pageSize;

  const rows = React.useMemo(
    () =>
      _.compact(_queueRead?.queues ?? []).map((queue, index) => ({
        id: `${queue?.vhost ?? "/"}-${queue?.name ?? "queue"}-${index}`,
        sno: `${_skip + index + 1}.`,
        name: queue?.name ?? "-",
        trunk: queue?.trunk ?? "-",
        ownerType: queue?.ownerType ?? null,
        vhost: queue?.vhost ?? "/",
        messages: Number(queue?.messages ?? 0),
        consumers: Number(queue?.consumers ?? 0),
        durable: Boolean(queue?.durable),
        autoDelete: Boolean(queue?.autoDelete),
      })),
    [_queueRead?.queues, _skip],
  );

  React.useEffect(() => {
    setSelectedIds([]);
    setSelectAll(false);
  }, [_queueRead?.queues]);

  const selectedIdsSet = React.useMemo(
    () => new Set(selectedIds),
    [selectedIds],
  );

  const _HandleCheckboxChange = React.useCallback((id) => {
    setSelectedIds((current) =>
      current.includes(id)
        ? current.filter((selectedId) => selectedId !== id)
        : [...current, id],
    );
  }, []);

  const _HandleSelectAll = React.useCallback(() => {
    setSelectAll((current) => {
      const nextSelectAll = !current;

      setSelectedIds(nextSelectAll ? rows.map((row) => row.id) : []);

      return nextSelectAll;
    });
  }, [rows]);

  const _HandleDeleteOpen = React.useCallback(
    (row) => {
      const selectedRows = rows.filter((item) => selectedIdsSet.has(item.id));
      const nextRowsToDelete = _.uniq(
        [row, ...selectedRows],
        false,
        (item) => item?.id,
      );

      setSelectedRow(row);
      setRowsToDelete(nextRowsToDelete);
      onDeleteModalOpen();
    },
    [onDeleteModalOpen, rows, selectedIdsSet],
  );

  const _DeleteSubHeading = React.useMemo(() => {
    if (1 < rowsToDelete.length)
      return `Delete ${rowsToDelete.length} selected queues? This action cannot be undone.`;

    return `Delete queue "${selectedRow?.name ?? ""}"? This action cannot be undone.`;
  }, [rowsToDelete.length, selectedRow?.name]);

  const columns = React.useMemo(
    () => [
      {
        field: "checkbox",
        headerName: "",
        width: 60,
        sortable: false,
        filterable: false,
        align: "center",
        headerAlign: "center",
        disableColumnMenu: true,
        renderHeader: () => (
          <Checkbox
            bg="gray.300"
            onChange={_HandleSelectAll}
            isChecked={selectAll}
            isDisabled={_.isEmpty(rows)}
          />
        ),
        renderCell: ({ row }) => (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            height="100%"
          >
            <Checkbox
              bg="gray.300"
              isChecked={selectedIdsSet.has(row.id)}
              onChange={() => _HandleCheckboxChange(row.id)}
            />
          </Box>
        ),
      },
      {
        field: "sno",
        headerName: "S.No.",
        align: "center",
        headerAlign: "center",
        disableColumnMenu: true,
        width: 100,
      },
      {
        field: "id",
        headerName: "ID",
        align: "center",
        editable: false,
        sortable: false,
        filterable: false,
        headerAlign: "center",
        hide: true,
      },
      {
        field: "name",
        headerName: "Queue Name",
        description: "RabbitMQ queue name.",
        editable: false,
        sortable: true,
        filterable: true,
        align: "center",
        headerAlign: "center",
        flex: 1.4,
        minWidth: 260,
      },
      {
        field: "trunk",
        headerName: "Trunk",
        description: "Customer or vendor account that owns the queue.",
        editable: false,
        sortable: true,
        filterable: true,
        align: "center",
        headerAlign: "center",
        flex: 1,
        minWidth: 180,
      },
      {
        field: "messages",
        headerName: "Messages",
        description: "Total queue messages.",
        editable: false,
        sortable: true,
        filterable: false,
        align: "center",
        headerAlign: "center",
        flex: 0.7,
        minWidth: 130,
        renderCell: ({ row }) => _FormatNumber(row.messages),
      },
      {
        field: "consumers",
        headerName: "Consumers",
        description: "Active consumers.",
        editable: false,
        sortable: true,
        filterable: false,
        align: "center",
        headerAlign: "center",
        flex: 0.7,
        minWidth: 130,
        renderCell: ({ row }) => _FormatNumber(row.consumers),
      },
      {
        field: "durable",
        headerName: "Durable",
        description: "Whether queue is durable.",
        editable: false,
        sortable: true,
        filterable: false,
        align: "center",
        headerAlign: "center",
        flex: 0.6,
        minWidth: 120,
        renderCell: ({ row }) => (row.durable ? "Yes" : "No"),
      },
      {
        field: "autoDelete",
        headerName: "Auto Delete",
        description: "Whether queue auto deletes.",
        editable: false,
        sortable: true,
        filterable: false,
        align: "center",
        headerAlign: "center",
        flex: 0.7,
        minWidth: 130,
        renderCell: ({ row }) => (row.autoDelete ? "Yes" : "No"),
      },
      {
        field: "action",
        headerName: "Actions",
        description: "Actions for queue.",
        editable: false,
        sortable: false,
        filterable: false,
        align: "center",
        headerAlign: "center",
        flex: 0.6,
        minWidth: 130,
        disableColumnMenu: true,
        renderCell: ({ row }) => (
          <Menu placement="left">
            <MenuButton
              as={Button}
              margin="auto"
              borderRadius="8px"
              color="rgba(0, 0, 0, 0.54)"
              bg="transparent"
            >
              <HiPencil size={18} />
            </MenuButton>

            <MenuList
              className="dropDownMenu"
              maxHeight="400px"
              overflow="auto"
              minW="120px"
              w="120px"
              p="4px"
            >
              <MenuItem
                display="flex"
                alignItems="center"
                gap="6px"
                px="12px"
                minH="36px"
                color="red.500"
                onClick={() => _HandleDeleteOpen(row)}
              >
                <HiTrash size={16} />
                <span>Delete</span>
              </MenuItem>
            </MenuList>
          </Menu>
        ),
      },
    ],
    [
      _HandleCheckboxChange,
      _HandleDeleteOpen,
      _HandleSelectAll,
      rows,
      selectAll,
      selectedIdsSet,
    ],
  );

  const slots = React.useMemo(
    () => ({
      loadingOverlay: () => (
        <TableSpinner
          isLoading={true}
          isEmpty={false}
          chopHeightFromHundredPercentage={
            _tableHeaderHeightRef?.current?.clientHeight
          }
        />
      ),
      noResultsOverlay: () => (
        <TableSpinner
          isLoading={false}
          isEmpty={true}
          chopHeightFromHundredPercentage={
            _tableHeaderHeightRef?.current?.clientHeight
          }
        />
      ),
      noRowsOverlay: () => (
        <TableSpinner
          isLoading={false}
          isEmpty={true}
          chopHeightFromHundredPercentage={
            _tableHeaderHeightRef?.current?.clientHeight
          }
        />
      ),
    }),
    [],
  );

  return (
    <>
      <Flex className="queue base" height="auto" flexDirection="column">
        <Flex
          display="flex"
          flex={1}
          flexDirection="column"
          height="100%"
          width="100%"
          minWidth={0}
          overflow="hidden"
        >
          <Flex
            bg="linear-Gradient(73deg,#390EF6 0%, #9244F4 100%)"
            borderRadius="20px 20px 0 0"
            flexDirection="row"
            flexWrap="wrap"
            alignItems="center"
            justifyContent="space-between"
            gap="12px"
            overflow="hidden"
            p={_isCurrentViewMobile ? "12px" : "22px"}
            flexShrink={0}
          >
            <Flex gap="2px" flexDir="column" minH="40px" justifyContent="center">
              <Text
                as="h3"
                fontSize={17}
                lineHeight="20px"
                fontWeight={500}
                display="flex"
                alignItems="center"
                color="white"
              >
                Queue Records
              </Text>
              <Text
                color="whiteAlpha.800"
                fontSize="13px"
                lineHeight="18px"
                fontWeight="500"
              >
                RabbitMQ queues available on this server.
              </Text>
            </Flex>
            <Tooltip label="Refresh queues." fontSize="sm">
              <Button
                borderRadius="8px"
                fontWeight="450"
                minW="20px"
                backgroundColor="purple.200"
                color="purple"
                _hover={{ bg: "purple.800", color: "white" }}
                leftIcon={<HiArrowPath />}
                isDisabled={isRefreshing}
                onClick={_HandleRefresh}
              >
                Refresh
              </Button>
            </Tooltip>
          </Flex>

          <Tabs
            variant="enclosed"
            index={ownerTypeIndex}
            onChange={_HandleOwnerTypeChange}
            isFitted
            w="100%"
            flexShrink={0}
          >
            <TabList
              bg="linear-Gradient(73deg,#390EF6 0%, #9244F4 100%)"
              border="none"
              overflowX={_isCurrentViewMobile ? "auto" : "hidden"}
            >
              {OWNER_TYPES.map((type) => (
                <Tab
                  aria-label={`${type.toLowerCase()} queues`}
                  bg="transparent"
                  color="white"
                  key={type}
                  minH="56px"
                  p="16px"
                  _active={{ bg: "transparent" }}
                  _focus={{ boxShadow: "none", bg: "transparent" }}
                  _hover={{ bg: "transparent" }}
                  _selected={{ bg: "transparent", textDecoration: "none" }}
                  textDecoration="line-through"
                  style={{ ...tabStyles, border: "none", borderRadius: 0 }}
                >
                  {type}
                </Tab>
              ))}
            </TabList>
          </Tabs>

          <Flex
            height="calc(100vh - 300px)"
            minHeight="320px"
            bg="white"
            flexDirection="column"
            border="2px solid #3A0FF6"
            borderTop="none"
            borderBottomRadius="15px"
            overflow="hidden"
          >
            {!_hasSuccessfulStatus ? (
              <Flex
                align="center"
                justify="center"
                border="1px solid rgba(238, 93, 80, 0.22)"
                borderRadius="8px"
                bg="rgba(238, 93, 80, 0.06)"
                minH="110px"
                flex={1}
                px="18px"
              >
                <Text color="red.500" fontWeight="700" textAlign="center">
                  {_queueRead?.message ?? "Unable to read RabbitMQ queues."}
                </Text>
              </Flex>
            ) : (
              <DataGrid
                rows={rows}
                columns={columns}
                rowHeight={48}
                filterMode="client"
                disableColumnSorting={false}
                filterModel={filterModel}
                onFilterModelChange={_setFilterDebounced}
                paginationModel={paginationModel}
                onPaginationModelChange={_HandlePaginationModelChange}
                paginationMode="server"
                rowCount={rowCount}
                pageSizeOptions={[10, 30, 50, 100]}
                slots={slots}
                disableRowSelectionOnClick
                columnVisibilityModel={columnVisibilityModel}
                onColumnVisibilityModelChange={_HandleColumnVisibilityModelChange}
                loading={_QueueReadQuery?.loading}
                sx={{
                  flex: 1,
                  "& .MuiDataGrid-main": {
                    border: "none",
                    borderRadius: 0,
                  },
                }}
              />
            )}
          </Flex>
        </Flex>
      </Flex>

      <Modal
        size="md"
        title="Queue Delete"
        isOpen={isDeleteModalOpen}
        onClose={_HandleDeleteClose}
      >
        <DeleteCard
          subHeading={_DeleteSubHeading}
          onClose={_HandleDeleteClose}
          disabled={MutationQueueDeleteResponse.loading}
          onConfirm={_HandleDeleteConfirm}
          isLoading={MutationQueueDeleteResponse.loading}
        />
      </Modal>
    </>
  );
};

/*
 * PROPTYPES
 */
Index.propTypes = {};

/*
 * EXPORT
 */
export default React.memo(Index);
